import { useEffect, useRef, useState } from 'react';
import registry, { Shortcut } from './registry';

/**
 * Registers a keyboard shortcut for as long as the component is mounted.
 *
 * The shortcut object may be rebuilt on every render - its `run` usually
 * closes over state - and the registry always calls the latest one, so
 * callers need neither `useCallback` nor refs of their own. Its place in the
 * order is fixed at mount, which is what makes "the most recently mounted
 * wins" mean something.
 *
 * `enabled` false unregisters; registering again on true puts it at the
 * front of the order, as a fresh mount would.
 */
export default function useShortcut(shortcut: Shortcut, enabled = true) {
  const latest = useRef(shortcut);
  latest.current = shortcut;

  useEffect(
    function registerShortcut() {
      if (!enabled) {
        return;
      }

      return registry.register(() => latest.current);
    },
    [enabled],
  );
}

/**
 * Registers a list of shortcuts at once - the navigation chords, one per
 * page - in list order, so the first is the oldest. The list may be rebuilt
 * on render like a single shortcut may; only a change in its length
 * re-registers.
 */
export function useShortcuts(shortcuts: Shortcut[]) {
  const latest = useRef(shortcuts);
  latest.current = shortcuts;

  const count = shortcuts.length;

  useEffect(
    function registerShortcuts() {
      const disposers = latest.current.map((_, i) => registry.register(() => latest.current[i]));

      return () => {
        disposers.forEach((dispose) => dispose());
      };
    },
    [count],
  );
}

/**
 * What is registered right now, for the overlay, and the chord prefix that
 * is waiting for its second key.
 */
export function useShortcutList() {
  const [state, setState] = useState(() => ({
    shortcuts: registry.list(),
    pendingPrefix: registry.pendingPrefix(),
  }));

  useEffect(function followRegistry() {
    function update() {
      setState({
        shortcuts: registry.list(),
        pendingPrefix: registry.pendingPrefix(),
      });
    }

    update();

    return registry.subscribe(update);
  }, []);

  return state;
}
